"use client";

import { useCallback, useEffect, useState } from "react";
import { Crisp } from "crisp-sdk-web";
import { useSession } from "@/component/SessionProvider";
import { usePathname } from "next/navigation";
import styled from "@emotion/styled";
import chatIcon from "@/assets/img/icons/chat.png";
import xIcon from "@/assets/img/icons/letter-x.png";
import Image from "next/image";

const crispId =
  process.env.NEXT_PUBLIC_CRISP_ID || "cfbd329d-8a85-4f98-9679-1150707bd290";

const ChatButton = styled.button<{ isHome: boolean }>`
  position: fixed;
  bottom: ${({ isHome }) => (isHome ? "10px" : "65px")};
  right: 10px;
  z-index: 1000;
  height: 60px;
  width: 60px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 0 10px rgba(95, 12, 12, 0.2);
  transition: all 0.3s ease-in-out;
  background: linear-gradient(180deg, #e275ff 20%, rgba(1, 3, 20, 0.99) 100%);

  @media (min-width: 992px) {
    bottom: 10px;
  }

  @media (min-width: 480px) {
    z-index: 1000000000;
  }
`;

export const CrispChat = () => {
  const { session } = useSession();
  const [crispEl, setCrispEl] = useState<Element | null>(null);
  // const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    Crisp.configure(crispId);
    // setIsMounted(true);

    // Wait for Crisp to load before fetching elements
    const interval = setInterval(() => {
      if (crispEl) {
        return clearInterval(interval);
      }

      const _crispEl = document.getElementsByClassName("crisp-client")?.[0];
      if (_crispEl) {
        setCrispEl(_crispEl);
        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval); // Cleanup on unmount
  }, [crispEl]);

  useEffect(() => {
    if (!session?.user) return;
    const user = session.user;

    Crisp.session.setData({
      user_id: user.id,
      email: user.email,
    });

    // Crisp.user.setEmail(user.email);
    Crisp.user.setNickname(user.name || "");
  }, [session?.user]);

  const path = usePathname();
  const isHome = path === "/";

  const getStyle = useCallback(() => {
    return "bottom: 1000px !important";
  }, [isHome]);

  useEffect(() => {
    if (!crispEl) return;

    setTimeout(() => {
      const anchorElements = crispEl.getElementsByTagName("a");
      const branding = crispEl.querySelector("a[href*='crisp.chat']");
      const chatButton = anchorElements?.[anchorElements.length - 1];

      chatButton?.setAttribute("style", getStyle());
      branding?.setAttribute("style", "display: none !important");
    }, 300);
  }, [crispEl, getStyle]);

  const [isOpen, setIsOpen] = useState(
    () => !!crispEl && Crisp.chat.isChatOpened()
  );

  const getIsOpen = useCallback(() => {
    if (!crispEl) return;

    return Crisp.chat.isChatOpened();
  }, [crispEl]);

  useEffect(() => {
    if (!crispEl) return;

    Crisp.chat.onChatClosed(() => {
      setIsOpen(false);
    });

    const interval = setInterval(() => {
      const isOpen = getIsOpen();
      if (isOpen !== undefined) {
        setIsOpen(isOpen);
        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval); // Cleanup on unmount
  }, [crispEl, getIsOpen]);

  const handleClick = () => {
    if (!crispEl) return;
    const isOpen = Crisp.chat.isChatOpened();

    const anchorElements = crispEl?.getElementsByTagName("a");
    const chatButton = anchorElements?.[anchorElements.length - 1];
    chatButton?.setAttribute("style", getStyle());

    if (isOpen) {
      Crisp.chat.close();
      Crisp.chat.hide();
      setIsOpen(false);
    } else {
      Crisp.chat.show();
      Crisp.chat.open();
      setIsOpen(true);
    }
  };

  return crispEl ? (
    <ChatButton isHome={isHome} id="crisp-chat-button" onClick={handleClick}>
      <Image src={isOpen ? xIcon : chatIcon} alt="chat" width={30} />
    </ChatButton>
  ) : null; // This is the button that will be styled
};
